import React from 'react';

interface TeamSnapshot {
    team_abbrev: string;
    team_name: string;
    conference: string;
    division?: string;
    division_rank?: number;
    playoff_position?: string;
    points: number;
    wins: number;
    losses: number;
    otl: number;
    rw?: number;
    regulation_wins: number;
    games_played: number;
    games_remaining: number;
    is_eliminated: boolean;
    elimination_date: string | null;
    post_elim_points: number;
    post_elim_wins: number;
    post_elim_losses: number;
    post_elim_otl: number;
    post_elim_row: number;
    post_elim_games: number;
    post_elim_record: string | null;
    larry_pool_rank: number | null;
    max_possible_points: number;
    points_to_larry_line: number;
    has_clinched: boolean;
}

interface StandingsData {
    league: string;
    season: string;
    last_updated: string | null;
    larry_pool: TeamSnapshot[];
    still_alive: TeamSnapshot[];
}

interface Props {
    data: StandingsData | null;
    league: string;
    loading: boolean;
}

// ── Elimination Risk Strip ──────────────────────────────────────────────────
//
// Visual: [■■■■■■░░░░░] where red/amber = pts needed, green = safety margin
//
// Math:
//   remaining  = max points available in remaining games (2*GP for NHL, 1*GP for NBA)
//   gap        = points_to_larry_line (positive = above cutoff at max; negative = eliminated)
//   ptsNeeded  = remaining - gap   (how many pts needed to equal cutoff's current ceiling)
//   urgency    = ptsNeeded / remaining  (0=safe, 1=need every pt, >1=eliminated)
//
// Color ramp on urgency:
//   < 0   → already above cutoff right now (clinched safe)
//   0–0.3 → green
//   0.3–0.6 → yellow
//   0.6–0.85 → amber
//   0.85–1.0 → red
//   ≥ 1.0 → eliminated (shown as gray row in parent)

interface ElimStripProps {
    team: TeamSnapshot;
    isNHL: boolean;
    cutoffLabel: string; // e.g. "8th in East"
}

const ElimStrip: React.FC<ElimStripProps> = ({ team, isNHL, cutoffLabel }) => {
    if (team.has_clinched) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: '#059669', flexShrink: 0
                }} />
                <span style={{ color: '#059669', fontWeight: 700, fontSize: '0.85rem' }}>Safe</span>
            </div>
        );
    }

    const current = isNHL ? team.points : team.wins;
    const remaining = isNHL ? 2 * team.games_remaining : team.games_remaining;
    const gap = team.points_to_larry_line ?? 0;
    const maxPossible = current + remaining;
    const cutoff = maxPossible - gap;
    const ptsNeeded = Math.max(0, cutoff - current);
    const urgency = remaining > 0 ? (remaining - gap) / remaining : (gap <= 0 ? 1 : 0);
    const isEliminated = urgency >= 1;

    // Safety pct = portion of remaining that is safety margin (green bar)
    const safetyPct = Math.max(0, Math.min(100, (gap / remaining) * 100));
    // Needed pct = portion still needed to reach cutoff (red/amber bar)
    const neededPct = Math.max(0, Math.min(100, 100 - safetyPct));

    const urgencyColor =
        isEliminated    ? '#9ca3af' :
        urgency >= 0.85 ? '#dc2626' :
        urgency >= 0.6  ? '#d97706' :
        urgency >= 0.3  ? '#eab308' :
                          '#059669';

    const unit = isNHL ? 'pts' : 'wins';
    const neededLabel = isEliminated
        ? 'Eliminated'
        : ptsNeeded === 0
            ? `Safe now`
            : `Need ${ptsNeeded} ${unit}`;

    // Tooltip — the auditable formula
    const tooltipText = isEliminated
        ? `${team.team_name} is mathematically eliminated. Max possible: ${maxPossible} ${unit}, cutoff: ${cutoff} ${unit}.`
        : `${team.team_name}: ${current} ${unit} | ${team.games_remaining} GP left | Max possible: ${maxPossible} ${unit}\n` +
          `Cutoff (${cutoffLabel}): ~${cutoff} ${unit}\n` +
          `Need ${ptsNeeded} ${unit} from ${team.games_remaining} remaining games to escape lottery zone.\n` +
          `Urgency: ${Math.round(urgency * 100)}% of remaining games must produce ${unit}.`;

    return (
        <div title={tooltipText} style={{ minWidth: 130, cursor: 'help' }}>
            {/* Top line: current pts — label — max pts */}
            <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '0.72rem', marginBottom: 3, color: '#6b7280'
            }}>
                <span style={{ fontWeight: 700, color: '#111827' }}>{current}</span>
                <span style={{ color: urgencyColor, fontWeight: 700, fontSize: '0.72rem' }}>
                    {neededLabel}
                </span>
                <span>{maxPossible}</span>
            </div>

            {/* Progress bar */}
            <div style={{
                position: 'relative', height: 7,
                background: '#e5e7eb', borderRadius: 4, overflow: 'hidden'
            }}>
                {/* Red/amber fill = pts still needed */}
                {neededPct > 0 && (
                    <div style={{
                        position: 'absolute', left: 0,
                        width: `${neededPct}%`, height: '100%',
                        background: urgencyColor,
                        borderRadius: 4
                    }} />
                )}
                {/* Green fill = safety margin (right side) */}
                {safetyPct > 0 && (
                    <div style={{
                        position: 'absolute', right: 0,
                        width: `${safetyPct}%`, height: '100%',
                        background: '#059669',
                        borderRadius: 4
                    }} />
                )}
            </div>
        </div>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────

const LarryPoolStandings: React.FC<Props> = ({ data, league, loading }) => {
    if (loading) {
        return <div className="larry-loading">Loading {league.toUpperCase()} standings...</div>;
    }

    if (!data) {
        return (
            <div className="larry-empty">
                No standings data available yet. Data refreshes daily at 6:00 AM EST.
            </div>
        );
    }

    const isNHL = league === 'nhl';

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const lastUpdated = data.last_updated
        ? new Date(data.last_updated).toLocaleString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
              hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
          })
        : 'Not yet updated';

    // Determine cutoff label per conference for tooltip context
    const cutoffLabel = isNHL ? '8th in conference' : '10th in conference';

    const sortedAlive = [...data.still_alive].sort((a, b) => {
        if (a.has_clinched && !b.has_clinched) return 1;
        if (!a.has_clinched && b.has_clinched) return -1;
        const aGap = a.points_to_larry_line ?? 999;
        const bGap = b.points_to_larry_line ?? 999;
        if (aGap !== bGap) return aGap - bGap;
        return isNHL ? (a.points - b.points) : (a.wins - b.wins);
    });

    return (
        <>
            {/* Larry Pool - Eliminated Teams */}
            <div className="standings-section">
                <h2>{data.season} Larry Pool Standings</h2>
                <div className="last-updated">Updated: {lastUpdated}</div>

                {data.larry_pool.length === 0 ? (
                    <div className="larry-empty">
                        No teams have been mathematically eliminated yet this season.
                        Check back as the season progresses!
                    </div>
                ) : (
                    <table className="standings-table">
                        <thead>
                            <tr>
                                <th>Draft Pick</th>
                                <th>Team</th>
                                <th>Conf</th>
                                <th>Eliminated</th>
                                <th>LP GP</th>
                                {isNHL ? (
                                    <>
                                        <th>LP Pts</th>
                                        <th>Record</th>
                                        <th>ROW</th>
                                        <th>Pts/Game</th>
                                    </>
                                ) : (
                                    <>
                                        <th>LP Wins</th>
                                        <th>Record</th>
                                        <th>Win%</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {data.larry_pool.map((team) => {
                                const rank = team.larry_pool_rank || 0;
                                const rankClass = rank <= 3 ? `rank-${rank}` : '';
                                const ptsPerGame = team.post_elim_games > 0
                                    ? (team.post_elim_points / team.post_elim_games).toFixed(2)
                                    : '0.00';
                                const winPct = team.post_elim_games > 0
                                    ? (team.post_elim_wins / team.post_elim_games * 100).toFixed(1) + '%'
                                    : '—';

                                return (
                                    <tr key={team.team_abbrev}>
                                        <td className={`rank-cell ${rankClass}`}>#{rank}</td>
                                        <td className="team-cell">{team.team_name}</td>
                                        <td className="num-cell">{team.conference}</td>
                                        <td className="num-cell">{formatDate(team.elimination_date)}</td>
                                        <td className="num-cell">{team.post_elim_games}</td>
                                        {isNHL ? (
                                            <>
                                                <td className="num-cell" style={{ fontWeight: 700 }}>{team.post_elim_points}</td>
                                                <td className="num-cell">{team.post_elim_record || '—'}</td>
                                                <td className="num-cell">{team.post_elim_row}</td>
                                                <td className="num-cell">{ptsPerGame}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="num-cell" style={{ fontWeight: 700 }}>{team.post_elim_wins}</td>
                                                <td className="num-cell">{team.post_elim_record || '—'}</td>
                                                <td className="num-cell">{winPct}</td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Still Alive */}
            <div className="standings-section">
                <h2>Still Alive</h2>
                <p style={{ color: '#666', fontSize: '0.88rem', margin: '-6px 0 8px' }}>
                    Sorted by elimination risk. Hover the progress bar for the exact math.
                    {isNHL
                        ? ' Red = needs most/all remaining pts, green = already safe. NHL tiebreaker: Points → RW → ROW.'
                        : ' Red = needs most/all remaining wins, green = already safe.'}
                </p>

                {/* Urgency legend */}
                <div style={{
                    display: 'flex', gap: 16, flexWrap: 'wrap',
                    fontSize: '0.78rem', marginBottom: 12, color: '#6b7280'
                }}>
                    {[
                        { color: '#059669', label: 'Safe (< 30% needed)' },
                        { color: '#eab308', label: 'Watch (30–60%)' },
                        { color: '#d97706', label: 'Danger (60–85%)' },
                        { color: '#dc2626', label: 'Critical (> 85%)' },
                    ].map(({ color, label }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>

                {data.still_alive.length === 0 ? (
                    <div className="larry-empty">All teams have been eliminated.</div>
                ) : (
                    <table className="standings-table">
                        <thead>
                            <tr>
                                <th>Team</th>
                                {isNHL ? (
                                    <>
                                        <th>Div</th>
                                        <th>Position</th>
                                        <th>Points</th>
                                        <th>Record</th>
                                        <th title="Regulation Wins — tiebreaker #1 (NHL Rule 26.1)">RW</th>
                                        <th title="Regulation + OT Wins — tiebreaker #2">ROW</th>
                                        <th>GP</th>
                                        <th>Rem</th>
                                        <th>LL Status ⓘ</th>
                                    </>
                                ) : (
                                    <>
                                        <th>Conf</th>
                                        <th>Wins</th>
                                        <th>Losses</th>
                                        <th>GP</th>
                                        <th>Remaining</th>
                                        <th>LL Status ⓘ</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAlive.map((team) => {
                                const gap = team.points_to_larry_line ?? 0;
                                const remaining = isNHL ? 2 * team.games_remaining : team.games_remaining;
                                const urgency = remaining > 0 ? (remaining - gap) / remaining : (gap <= 0 ? 1 : 0);
                                const rowBg =
                                    team.has_clinched      ? '' :
                                    urgency >= 0.85        ? 'danger' :
                                    urgency >= 0.3         ? 'warning' :
                                                             '';

                                return (
                                    <tr key={team.team_abbrev} className={rowBg}>
                                        <td className="team-cell">{team.team_name}</td>
                                        {isNHL ? (
                                            <>
                                                <td className="num-cell">{team.division || team.conference}</td>
                                                <td className="num-cell" style={{
                                                    fontWeight: 600,
                                                    color: team.playoff_position?.startsWith('WC') ? '#d97706'
                                                        : team.playoff_position ? '#059669' : '#dc2626'
                                                }}>
                                                    {team.playoff_position || 'Out'}
                                                </td>
                                                <td className="num-cell" style={{ fontWeight: 700 }}>{team.points}</td>
                                                <td className="num-cell">{team.wins}-{team.losses}-{team.otl}</td>
                                                <td className="num-cell">{team.rw ?? '—'}</td>
                                                <td className="num-cell">{team.regulation_wins ?? '—'}</td>
                                                <td className="num-cell">{team.games_played}</td>
                                                <td className="num-cell">{team.games_remaining}</td>
                                                <td style={{ minWidth: 140 }}>
                                                    <ElimStrip team={team} isNHL={isNHL} cutoffLabel={cutoffLabel} />
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="num-cell">{team.conference}</td>
                                                <td className="num-cell" style={{ fontWeight: 700 }}>{team.wins}</td>
                                                <td className="num-cell">{team.losses}</td>
                                                <td className="num-cell">{team.games_played}</td>
                                                <td className="num-cell">{team.games_remaining}</td>
                                                <td style={{ minWidth: 140 }}>
                                                    <ElimStrip team={team} isNHL={isNHL} cutoffLabel={cutoffLabel} />
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default LarryPoolStandings;
